import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Grid, List, Typography } from '@mui/material';
import { Fab } from '@biq/apex-ui';
import {
    AttributeAssociationItem,
    defaultAttributeAssociationItem,
} from '../../pages/edge-definitions/edge-definition-helper';
import {
    checkForInvalidProperties,
    PairedInputSelectionWidgetProps,
    pairedInputSelectionWidgetStyles,
} from './paired-input-selection-helper';
import { PairedInputSelection } from './PairedInputSelection';

export const PairedInputSelectionWidget = ({
    title,
    addButtonLabel,
    attributeAssociationList,
    handleAttributeAssociationsChanged,
    duplicateErrorMessage,
    leftSideInputLabel,
    rightSideInputLabel,
    checkForDuplicates,
    emptyErrorMessage = '',
    leftSideInputStyles,
    rightSideInputStyles,
    displayEmptyState,
    emptyStateHeadingText,
    emptyStateBodyText,
    emptyStateIcon,
    emptyStateIconStyles,
    titleContainerStyles,
    emptyStateMainContainerStyles,
    emptyStateItemContainerStyles,
    emptyStateHeadingTextContainerStyles,
    emptyStateBodyTextContainerStyles,
    emptyStateHeadingTextStyles,
    emptyStateBodyTextStyles,
    customInputSelectionStyles,
    addButtonContainerStyles,
    addButtonStyles,
    widgetMainContainerStyles,
    deleteButtonContainerStyles,
    deleteButtonStyles,
}: PairedInputSelectionWidgetProps): React.JSX.Element => {
    const { isListValid, updatedList } = checkForInvalidProperties(
        attributeAssociationList
    );
    const onAddButtonClick = (): void => {
        if (isListValid) {
            handleAttributeAssociationsChanged([
                ...attributeAssociationList,
                {
                    ...defaultAttributeAssociationItem,
                    id: crypto.randomUUID(),
                },
            ]);
        } else {
            handleAttributeAssociationsChanged(updatedList);
        }
    };

    const possiblyGetEmptyErrorMessage = (isEmptyValue: boolean): string => {
        if (isEmptyValue) {
            return emptyErrorMessage;
        }
        return '';
    };

    const getUpdatedItemWithPossibleErrorStates = (
        currentItem: AttributeAssociationItem,
        isLeftSideDuplicated: boolean,
        isLeftAndRightPairDuplicated: boolean
    ): AttributeAssociationItem => {
        const leftSideValueValid: boolean =
            !!currentItem.attributeAssociation.leftSideValue;
        const rightSideValueValid: boolean =
            !!currentItem.attributeAssociation.rightSideValue;

        return {
            ...currentItem,
            leftSideError: isLeftSideDuplicated || !leftSideValueValid,
            leftSideErrorMessage: isLeftSideDuplicated
                ? duplicateErrorMessage
                : possiblyGetEmptyErrorMessage(!leftSideValueValid),
            rightSideError:
                isLeftAndRightPairDuplicated || !rightSideValueValid,
            rightSideErrorMessage: isLeftAndRightPairDuplicated
                ? duplicateErrorMessage
                : possiblyGetEmptyErrorMessage(!rightSideValueValid),
        };
    };

    const possiblyCheckForDuplicatedAndEmptyItems = (
        list: AttributeAssociationItem[]
    ): AttributeAssociationItem[] => {
        if (!list.length) return [];

        const propertyTracker: any = {};

        /**
         * The following code checks for potential duplicates via a map.
         *
         * 1. If the current item does not exist in propertyTracker, then we simply add it to the map with the left value as the key.
         * 2. If the current item already exists in propertyTracker, then we potentially add the duplication error based on whether
         *    checkForDuplicates has been set to true. Since it already exists in the map, we want to add it to propertyTracker with
         *    the (left value and the index) as the key to prevent replacing the already existing item. This will allow us to keep adding
         *    more items with the same left value in case checkForDuplicates is set to false.
         */

        list.forEach((listItem: AttributeAssociationItem, index: number) => {
            if (propertyTracker[listItem.attributeAssociation.leftSideValue]) {
                let isLeftSideDuplicated: boolean = false;
                let isLeftAndRightPairDuplicated: boolean = false;

                // only try to check for duplicates if checkForDuplicates is true.
                if (checkForDuplicates) {
                    const savedItem: AttributeAssociationItem =
                        propertyTracker[
                            listItem.attributeAssociation.leftSideValue
                        ];

                    // since we are checking for duplicates and the left value is the key in propertyTracker, then isLeftSideDuplicated is always true.
                    isLeftSideDuplicated = true;
                    isLeftAndRightPairDuplicated =
                        listItem.attributeAssociation.rightSideValue ===
                        savedItem.attributeAssociation.rightSideValue;
                }

                const newItem: AttributeAssociationItem =
                    getUpdatedItemWithPossibleErrorStates(
                        listItem,
                        isLeftSideDuplicated,
                        isLeftAndRightPairDuplicated
                    );
                propertyTracker[
                    `${newItem.attributeAssociation.leftSideValue}_${index}`
                ] = newItem;
            } else {
                const newItem: AttributeAssociationItem =
                    getUpdatedItemWithPossibleErrorStates(
                        listItem,
                        false,
                        false
                    );
                propertyTracker[newItem.attributeAssociation.leftSideValue] =
                    newItem;
            }
        });

        return Object.values(propertyTracker);
    };

    const updateItem = (
        idToUpdate: string,
        updatedItem: AttributeAssociationItem
    ): void => {
        const updatedItems: AttributeAssociationItem[] =
            attributeAssociationList.map((item) =>
                idToUpdate === item.id ? updatedItem : item
            );
        handleAttributeAssociationsChanged(
            possiblyCheckForDuplicatedAndEmptyItems(updatedItems)
        );
    };

    const deleteItem = (idToDelete: string): void => {
        const updatedItems: AttributeAssociationItem[] =
            attributeAssociationList.filter(
                (association) => idToDelete !== association.id
            );
        handleAttributeAssociationsChanged(
            possiblyCheckForDuplicatedAndEmptyItems(updatedItems)
        );
    };

    return (
        <Grid sx={widgetMainContainerStyles}>
            <Grid sx={titleContainerStyles}>
                <Typography
                    sx={
                        pairedInputSelectionWidgetStyles.attributeAssociationTitle
                    }
                >
                    {title}
                </Typography>
            </Grid>
            <Grid sx={pairedInputSelectionWidgetStyles.bodyContainer}>
                <List sx={pairedInputSelectionWidgetStyles.listContainer}>
                    {attributeAssociationList.map((item) => (
                        <PairedInputSelection
                            key={item.id}
                            attributeAssociationItem={item}
                            handleRemove={(): void => deleteItem(item.id)}
                            onAttributeAssociationChanged={(value: any): void =>
                                updateItem(item.id, value)
                            }
                            leftSideInputLabel={leftSideInputLabel}
                            rightSideInputLabel={rightSideInputLabel}
                            emptyErrorMessage={emptyErrorMessage}
                            leftSideInputStyles={leftSideInputStyles}
                            rightSideInputStyles={rightSideInputStyles}
                            deleteButtonContainerStyles={
                                deleteButtonContainerStyles
                            }
                            deleteButtonStyles={deleteButtonStyles}
                            customInputSelectionStyles={
                                customInputSelectionStyles
                            }
                        />
                    ))}
                </List>
                <Grid sx={addButtonContainerStyles}>
                    <Fab
                        type='button'
                        sx={addButtonStyles}
                        icon={<AddIcon />}
                        color='secondary'
                        label={addButtonLabel}
                        variant='extended'
                        size='small'
                        onClick={onAddButtonClick}
                    />
                </Grid>
                {attributeAssociationList.length === 0 && displayEmptyState && (
                    <Grid sx={emptyStateMainContainerStyles}>
                        <Grid sx={emptyStateItemContainerStyles}>
                            <img
                                src={emptyStateIcon}
                                alt='empty-state-paired-selection-icon'
                                style={emptyStateIconStyles}
                            />
                        </Grid>
                        <Grid sx={emptyStateHeadingTextContainerStyles}>
                            <Typography sx={emptyStateHeadingTextStyles}>
                                {emptyStateHeadingText}
                            </Typography>
                        </Grid>
                        <Grid sx={emptyStateBodyTextContainerStyles}>
                            <Typography sx={emptyStateBodyTextStyles}>
                                {emptyStateBodyText}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};
